import { Button, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import * as React from 'react';
import { useState } from 'react';
import type { UseFieldArrayAppend, UseFormGetValues } from 'react-hook-form';
import HeiUserAutocomplete from '../../../../Common/components/HeiUserSearch/HeiUserAutocomplete';
import type { ProjectFormType } from '../../../../Common/hooks/useCreateProject';
import type { User } from '../../../../Common/models/FormModels';
import { UserSearch } from '../../../../Common/models/UserSearch';
import { ToolManagerRole, ToolManagerRoles } from '../../api/models/ToolManagerDto';

const HEIWAY_DOMAIN = 'heiway.net';

interface AdminToolManagerSearchProps {
    onAddMember: (name: string, email: string, adPrinciple: string, role: ToolManagerRole) => void;
    existingMembers: Array<{ email: string; adPrinciple: string }>;
    disabled?: boolean;
    validateUserDomain?: boolean;
}

const AdminToolManagerSearch = ({
    onAddMember,
    existingMembers,
    disabled = false,
    validateUserDomain = true,
}: AdminToolManagerSearchProps): JSX.Element => {
    const autoComplete = React.useRef<HTMLInputElement>(null);
    const [selectedUser, setSelectedUser] = useState<UserSearch | null>(null);
    const [selectedRole, setSelectedRole] = useState<ToolManagerRole>('ToolOwner');

    const isSelectedUserValid =
        !validateUserDomain ||
        !selectedUser ||
        (selectedUser.adPrinciple?.toLowerCase().includes(HEIWAY_DOMAIN) ?? false);

    const handleUserChange = (user: UserSearch) => {
        setSelectedUser(user);
    };

    const handleAdd = () => {
        if (selectedUser && isSelectedUserValid) {
            onAddMember(
                selectedUser.name,
                selectedUser.email || selectedUser.adPrinciple,
                selectedUser.adPrinciple,
                selectedRole,
            );
            resetForm();
        }
    };

    const resetForm = () => {
        setSelectedUser(null);
        setSelectedRole('ToolOwner');
        if (autoComplete.current) {
            const clearButton = autoComplete.current.getElementsByClassName(
                'MuiAutocomplete-clearIndicator',
            )[0] as HTMLElement;
            if (clearButton) {
                clearButton.click();
            }
        }
    };

    const getUsers = ((field: string): User[] => {
        if (field === 'users') {
            return existingMembers.map(
                (member) =>
                    ({
                        name: '',
                        email: member.email,
                        adPrinciple: member.adPrinciple,
                        roles: [],
                    } as User),
            );
        }
        return [];
    }) as UseFormGetValues<ProjectFormType>;

    const addUser = (() => {
        // Required by HeiUserAutocomplete, but members are added after role selection.
    }) as UseFieldArrayAppend<ProjectFormType, 'users'>;

    const handleCreateNewAccount = (): void => {
        // Creating new accounts is not supported from tool manager management.
    };

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <HeiUserAutocomplete
                autoComplete={autoComplete}
                addUser={addUser}
                getUsers={getUsers}
                handleChange={handleUserChange}
                handleCreateNewAccount={handleCreateNewAccount}
                width='45%'
            />

            {selectedUser && (
                <>
                    {isSelectedUserValid ? (
                        <>
                            <FormControl size='small' sx={{ width: '180px' }} disabled={disabled}>
                                <InputLabel shrink>Role*</InputLabel>
                                <Select
                                    data-testid='tool-role-select'
                                    value={selectedRole}
                                    onChange={(event) =>
                                        setSelectedRole(event.target.value as ToolManagerRole)
                                    }
                                    label='Role'
                                    notched
                                >
                                    {(Object.keys(ToolManagerRoles) as ToolManagerRole[]).map(
                                        (role) => (
                                            <MenuItem
                                                data-testid={`tool-role-select-${role}`}
                                                key={role}
                                                value={role}
                                            >
                                                {ToolManagerRoles[role]}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>
                            <Button
                                variant='contained'
                                onClick={handleAdd}
                                sx={{ height: 40 }}
                                size='small'
                                disabled={disabled || !isSelectedUserValid}
                            >
                                ADD
                            </Button>

                            <Button
                                onClick={resetForm}
                                sx={{ height: 40 }}
                                size='small'
                                disabled={disabled}
                            >
                                CANCEL
                            </Button>
                        </>
                    ) : (
                        <FormControl size='small' error>
                            <FormHelperText>
                                Only users with a valid HeiWay ID can be selected.
                            </FormHelperText>
                        </FormControl>
                    )}
                </>
            )}
        </div>
    );
};

export default AdminToolManagerSearch;
